'use client';

import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../lib/cn';

/**
 * Button — primary action control. References only semantic tokens, so theme
 * presets + light/dark + area overrides restyle it for free.
 */
export const buttonVariants = cva(
  [
    'inline-flex items-center justify-center gap-2 select-none whitespace-nowrap',
    'font-sans font-semibold leading-none',
    'rounded-control border border-transparent',
    'transition-[background-color,border-color,transform,box-shadow] duration-[var(--duration-fast)] ease-[var(--ease-standard)]',
    'cursor-pointer active:scale-[0.97]',
    'disabled:pointer-events-none disabled:opacity-50 disabled:active:scale-100',
  ],
  {
    variants: {
      variant: {
        primary: 'bg-accent text-accent-fg hover:bg-accent-hover',
        secondary: 'bg-surface text-fg border-border-strong hover:bg-hover',
        ghost: 'bg-transparent text-fg-secondary hover:bg-hover',
        subtle: 'bg-accent-soft text-accent hover:bg-accent-line',
        danger: 'bg-danger text-danger-fg hover:bg-[var(--red-700)]',
      },
      size: {
        sm: 'h-[var(--control-height-sm)] px-3 text-sm',
        md: 'h-[var(--control-height)] px-4 text-base',
        lg: 'h-[var(--control-height-lg)] px-5 text-md',
      },
      block: { true: 'flex w-full', false: '' },
    },
    defaultVariants: { variant: 'primary', size: 'md', block: false },
  },
);

export interface ButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'color'>,
    VariantProps<typeof buttonVariants> {
  /** Render as the child element (Radix Slot) instead of a `<button>`. */
  asChild?: boolean;
  /** Shows a spinner and disables interaction. */
  loading?: boolean;
  iconLeft?: React.ReactNode;
  iconRight?: React.ReactNode;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant, size, block, asChild = false, loading = false, disabled, iconLeft, iconRight, children, type, ...rest },
  ref,
) {
  const Comp = asChild ? Slot : 'button';
  return (
    <Comp
      ref={ref}
      type={asChild ? undefined : (type ?? 'button')}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(buttonVariants({ variant, size, block }), className)}
      {...rest}
    >
      {loading ? <Spinner /> : iconLeft}
      {children}
      {!loading ? iconRight : null}
    </Comp>
  );
});

function Spinner() {
  return (
    <span
      aria-hidden="true"
      className="inline-block size-[1em] animate-spin rounded-full border-2 border-current border-t-transparent"
    />
  );
}
